import React, {
  useState,
  useCallback,
  useLayoutEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import cx from 'clsx';

import { IconInputRemove } from '@/assets/poll';
import { useSwitch } from '@/hooks';

import { InputProps, Input } from './Input';
import styles from './index.module.css';

type LabelInputProps = {
  label?: string;
  onRemoveClick?(): void;
  removeable?: boolean;
} & InputProps;

export const LabelInput = forwardRef(
  (
    {
      label,
      maxLength,
      value,
      defaultValue,
      onChange,
      onRemoveClick,
      className,
      removeable,
      disabled,
    }: LabelInputProps,
    ref,
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    useImperativeHandle(ref, () => ({
      focus() {
        inputRef?.current?.focus();
      },
    }));
    const { on: isFocused, turnOn: focus, turnOff: blur } = useSwitch();

    const handleClick = useCallback(() => {
      if (disabled) {
        return;
      }

      focus();

      setTimeout(() => {
        inputRef.current?.focus();
      });
    }, [disabled]);

    const [_value, setValue] = useState(value || defaultValue);

    useLayoutEffect(() => {
      setValue(value);
    }, [value]);

    const handleChange = useCallback(
      (v: string) => {
        if (disabled) {
          return;
        }

        setValue(v);
        onChange?.(v);
      },
      [disabled, onChange],
    );

    return (
      <div
        onClick={handleClick}
        className={cx(
          styles.labelInput,
          className,
          isFocused && styles.isFocused,
          disabled && styles.isDisabled,
        )}>
        <label dir="ltr" className={cx(styles.label, (isFocused || !!_value) && styles.isTop)}>
          <span className={styles.title}>{label}</span>
        </label>
        {disabled ? null : (
          <Input
            className={styles.innerInput}
            ref={inputRef}
            onFocus={focus}
            onBlur={blur}
            onChange={handleChange}
            value={_value}
            maxLength={maxLength}
          />
        )}
        {isFocused ? (
          maxLength ? (
            <span className={styles.valueCount}>
              {_value?.length || 0} / {maxLength}
            </span>
          ) : null
        ) : removeable ? (
          <IconInputRemove className={styles.removeIcon} onClick={onRemoveClick} />
        ) : null}
      </div>
    );
  },
);
